import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { UserState } from '@state/user.state';
import { buildCountryCitiesLUT, buildCountryList, City, Country } from 'app/@types/shared/world-cities';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { combineLatest, Observable, Subject, takeUntil } from 'rxjs';
import { IndustryService } from '@services/common/industry.service';
import { IndustryDto } from 'app/@types/dto/industry-dto.model';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { ToastService } from '@services/common/toast.service';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { OrganizationState } from '@state/organization.state';
import { QuestionnaireState } from '@state/questionnaire.state';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { OrganizationPricingPlanDto } from 'app/@types/dto/organization-pricing-plan-dto.model';
import { CurrencyDto } from 'app/@types/dto/currency-dto.model';
import { QuestionnaireHttpService } from '@services/datasources/questionnaire-http.service';
import { KpiDenominatorActions } from '@state/kpi-denominator.state';

@Component({
    selector: 'app-settings-form-entities',
    templateUrl: './settings-form-entities.component.html',
    standalone: false,
})
export class SettingsFormEntitiesComponent implements OnInit, OnDestroy {
    @Select(OrganizationState.getOrganization) organization$!: Observable<OrganizationDto>;
    @Select(OrganizationState.getEntities) entities$!: Observable<OrganizationEntityDto[]>;
    @Select(QuestionnaireState.getQuestionnaire) activeQuestionnaires$!: Observable<QuestionnaireDto>;
    @Select(OrganizationState.getQuestionnaires) questionnaires$!: Observable<QuestionnaireDto[] | undefined>;
    @Select(OrganizationState.getOrganizationPricingPlan) pricingPlan$!: Observable<OrganizationPricingPlanDto>;
    @Select(UserState.getCurrentUserClaims) userClaims$!: Observable<UserClaims>;

    /** Years with carbon data (non-deleted questionnaires), ascending — for KPI denominators. */
    public yearsWithData: number[] = [];
    /** Guards the KPI-denominator load against combineLatest re-emissions. */
    private lastKpiLoadKey = '';

    // @ts-expect-error: TODO: Remove ignore when types are fixed
    public organization: OrganizationDto;
    public organizationId: string = '';
    public entities: OrganizationEntityDto[] = [];
    public selectedEntity: OrganizationEntityDto | undefined = undefined;
    public addingEditingMode: boolean = false;
    private ngUnsubscribe = new Subject<void>();
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    public activeQuestionnaire: QuestionnaireDto;
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    public pricingPlan: OrganizationPricingPlanDto;
    public isLoading: boolean = true;
    public isOrgOwner: boolean = false;
    public canAddEntities: boolean = false;
    public currencies: CurrencyDto[] = [];

    private countries: Country[] = [];
    countryCitiesLUT: Map<string, City[]> = new Map<string, City[]>();
    public industries: IndustryDto[] = [];
    private industries$ = this.industryService.get();

    private pendingEditEntityId: string | null = null;

    constructor(
        private industryService: IndustryService,
        private translateService: CustomTranslateService,
        public toastService: ToastService,
        private ngZone: NgZone,
        private questionnaireService: QuestionnaireHttpService,
        private route: ActivatedRoute,
        private router: Router,
        private store: Store,
    ) {
        // Deep-link from outside (e.g. the measure-summary entity-row edit icon) drops an
        // `editEntityId` query param that we'll consume once entities load.
        this.pendingEditEntityId = this.route.snapshot.queryParamMap.get('editEntityId');
    }

    ngOnInit() {
        combineLatest([
            this.organization$,
            this.industries$,
            this.entities$,
            this.activeQuestionnaires$,
            this.pricingPlan$,
            this.userClaims$,
            this.questionnaires$,
        ])
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: ([organization, industries, entities, questionnaire, pricingplan, claims, questionnaires]) => {
                    if (organization && organization.uid && entities && industries) {
                        this.organization = organization;
                        this.organizationId = organization.uid;
                        this.entities = entities.filter((entity) => !entity.deleted);
                        this.industries = industries;
                        this.activeQuestionnaire = questionnaire;
                        this.pricingPlan = pricingplan;
                        this.isLoading = false;

                        this.yearsWithData = [
                            ...new Set((questionnaires ?? []).filter((q) => !q.deleted).map((q) => q.refYear)),
                        ].sort((a, b) => a - b);

                        // Load KPI denominator data once per (org, years-with-data) combination.
                        const kpiLoadKey = `${organization.uid}|${this.yearsWithData.join(',')}`;
                        if (this.yearsWithData.length > 0 && kpiLoadKey !== this.lastKpiLoadKey) {
                            this.lastKpiLoadKey = kpiLoadKey;
                            this.store.dispatch(new KpiDenominatorActions.LoadTypes(organization.uid));
                            this.store.dispatch(
                                new KpiDenominatorActions.LoadDenominators(organization.uid, this.yearsWithData),
                            );
                        }
                        this.isOrgOwner = claims.tasks.some(
                            (task) => task.name === 'orguser' && task.organization_id === claims.organization?.uid,
                        );
                        this.canAddEntities =
                            this.entities.length < this.pricingPlan.licenseDoc.max_entities && this.isOrgOwner;

                        this.questionnaireService.getCurrencies(organization.uid).subscribe((currencies) => {
                            this.currencies = currencies;
                        });

                        // Honour the deep-link from the matrix once and only once: open the
                        // edit form for the requested entity, then strip the query param so a
                        // refresh / back-navigation doesn't re-trigger the modal.
                        if (this.pendingEditEntityId) {
                            const targetEntity = this.entities.find((e) => e.id === this.pendingEditEntityId);
                            this.pendingEditEntityId = null;
                            if (targetEntity) {
                                this.addEditEntity(targetEntity);
                                this.router.navigate([], {
                                    relativeTo: this.route,
                                    queryParams: { editEntityId: null },
                                    queryParamsHandling: 'merge',
                                    replaceUrl: true,
                                });
                            }
                        }
                    }
                },
            });

        this.countries = buildCountryList();
        this.countryCitiesLUT = buildCountryCitiesLUT();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public addEditEntity(entity: OrganizationEntityDto | undefined = undefined): void {
        this.selectedEntity = entity;
        this.addingEditingMode = true;
    }

    public getIndustryDisplay(industryId: string): string {
        const res = this.industries.find((x) => x.industrySectorId === industryId);

        return res
            ? this.translateService.instant(`components.settingsForm.industry.${res.industrySectorLabel}`)
            : 'N/A';
    }

    onCloseEditEntity(event?: { status: boolean; translateKey: string; hasFteDecreased?: boolean }) {
        this.addingEditingMode = false;
        if (event) {
            this.ngZone.run(() => {
                if (event.status) {
                    this.toastService.showSuccess({
                        details: event.translateKey,
                    });
                    if (event.hasFteDecreased) {
                        this.toastService.showWarning({
                            details: 'components.settingsForm.entitySection.toasts.fteDecrease',
                            life: 8000,
                        });
                    }
                } else {
                    this.toastService.showError({
                        details: event.translateKey,
                    });
                }
            });
        }
    }
}
